import { router } from "@inertiajs/react"
import { useCallback, useEffect, useState } from "react"

export type Appearance = "light" | "dark" | "system"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  document.documentElement.classList.toggle("dark", isDark)
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const handleSystemThemeChange = () => {
  const currentAppearance = localStorage.getItem("appearance") as Appearance
  applyTheme(currentAppearance ?? "system")
}

/**
 * The server's value is authoritative — that is what makes the choice follow a
 * user to a second machine. localStorage is a first-paint cache so the page
 * does not flash light before the props arrive.
 */
export function initializeTheme() {
  // Default light: dark "Desk mode" is opt-in (see DESIGN.md).
  const cached = (localStorage.getItem("appearance") as Appearance) || "light"

  applyTheme(cached)

  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

export function useAppearance(serverAppearance?: Appearance) {
  const [appearance, setAppearance] = useState<Appearance>(
    serverAppearance ?? "light",
  )

  const updateAppearance = useCallback(
    (mode: Appearance, { persist = true }: { persist?: boolean } = {}) => {
      setAppearance(mode)
      // Cached locally so the next first paint is correct before props load.
      localStorage.setItem("appearance", mode)
      applyTheme(mode)

      if (persist) {
        // preserveState so the dropdown does not close out from under the
        // click, preserveScroll so a theme flip does not jump the page.
        router.patch(
          appearancePath(),
          { appearance: mode },
          { preserveState: true, preserveScroll: true, only: [] },
        )
      }
    },
    [],
  )

  // Apply whatever the server says on mount and whenever it changes, so a
  // choice made on another device takes effect here on the next page load.
  useEffect(() => {
    const next =
      serverAppearance ??
      ((localStorage.getItem("appearance") as Appearance | null) || "light")
    updateAppearance(next, { persist: false })

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [serverAppearance, updateAppearance])

  return { appearance, updateAppearance } as const
}

/**
 * Built by hand rather than imported from @/routes: this hook is also loaded on
 * the portal, where there is no account in scope and the generated helper would
 * throw on a missing :account_id.
 */
function appearancePath() {
  const accountUid = window.location.pathname.split("/")[1]
  return `/${accountUid}/appearance`
}
